<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import { useAttachmentUploads } from '@/composables/attachments/useAttachmentUploads';
import { useEntryAttachments } from '@/composables/attachments/useEntryAttachments';
import { getEntryAttachmentCards } from '@/composables/attachments/entryAttachment.utils';
import { useMobile } from '@/composables/useMobile';
import { useLoadWhenVisible } from '@/composables/useLoadWhenVisible';
import ComposerChip from '@/core/elements/ComposerChip.vue';
import ExternalMaterialSelectionPopover from '@/core/supportMeasures/ExternalMaterialSelectionPopover.vue';
import { ENTRY_ATTACHMENT_MAX_FILES_PER_ENTRY } from 'common/constants/file.upload.limits';
import {
  AttachmentContentType,
  EntryAttachmentType,
  type IEntryAttachmentTarget,
} from 'common/enum/entry.attachment.enum';
import { computed, ref, useTemplateRef } from 'vue';
import AttachmentFilePicker from './AttachmentFilePicker.vue';
import AttachmentGrid from './AttachmentGrid.vue';
import type { IAttachmentCardItem } from './AttachmentCard.vue';
import PendingAttachmentGrid from './PendingAttachmentGrid.vue';

const props = withDefaults(
  defineProps<{
    entryType: EntryAttachmentType;
    /** Draft entries use 0 and render local materials without loading saved attachments. */
    entryId: number;
    /** The entry's linked apps, shown as cards next to the files; omit to show files only. Changes are emitted, the owner saves them. */
    materials?: Components.Schemas.ExternalMaterialLinkDto[];
    readonly?: boolean;
    /** A draft composer supplies its own edit permission before an entry exists. */
    canEdit?: boolean;
    previewOnly?: boolean;
    /** Saved attachment cards are elevated; composers can opt out while editing. */
    elevated?: boolean;
    showActions?: boolean;
    maxItems?: number;
    /** Total file attachments known by the parent timeline DTO. */
    attachmentCount?: number;
    /** Includes student-specific attachments in a combined, read-only timeline preview. */
    additionalEntry?: IEntryAttachmentTarget;
  }>(),
  {
    materials: undefined,
    previewOnly: false,
    elevated: true,
    readonly: false,
    canEdit: undefined,
    showActions: true,
    maxItems: undefined,
    attachmentCount: undefined,
    additionalEntry: undefined,
  },
);
const emit = defineEmits<{ 'update:materials': [ids: number[]] }>();
const container = useTemplateRef<HTMLElement>('container');
const enabled = useLoadWhenVisible(container, { eager: () => !props.previewOnly });
const target = computed(() => ({ entryType: props.entryType, entryId: props.entryId }));
const { query, saving, refresh, removeFile } = useEntryAttachments(target, enabled);
const { pending, uploading, refreshFailed, add, remove, flush } = useAttachmentUploads();
const additionalTarget = computed(() => props.additionalEntry ?? target.value);
const { query: additionalQuery } = useEntryAttachments(
  additionalTarget,
  computed(() => enabled.value && props.readonly && props.additionalEntry != null),
);
const { isMobile } = useMobile();
const savedItemsDuringUpload = ref<Components.Schemas.EntryAttachmentItemDto[]>([]);
const showUploadSnapshot = computed(() => uploading.value || refreshFailed.value);
const materialIds = computed(() => (props.materials ?? []).map((material) => material.externalMaterialId));
const attachmentsReady = computed(
  () =>
    (props.entryId <= 0 || query.data.value != null) &&
    (!props.readonly || props.additionalEntry == null || additionalQuery.data.value != null),
);
const expectedAttachmentCount = computed(() => (props.attachmentCount ?? 0) + (props.materials?.length ?? 0));
const loadingItems = computed<IAttachmentCardItem[]>(() =>
  Array.from({ length: expectedAttachmentCount.value }, (_, index) => ({
    id: -(index + 1),
    type: AttachmentContentType.Image,
    name: '',
    imageSource: null,
    url: null,
  })),
);
const items = computed(() => {
  if (!attachmentsReady.value && !showUploadSnapshot.value) return [];
  return getEntryAttachmentCards({
    files: [
      ...(showUploadSnapshot.value ? savedItemsDuringUpload.value : (query.data.value?.items ?? [])),
      ...(props.readonly && props.additionalEntry ? (additionalQuery.data.value?.items ?? []) : []),
    ],
    materials: props.materials ?? [],
  });
});
const expanded = ref(false);
const expanding = ref(false);
// Use the same limit for placeholders and loaded cards on mobile and desktop.
const previewLimit = computed(() => {
  if (expanded.value || props.maxItems == null) return undefined;
  return isMobile.value ? Math.min(props.maxItems, 2) : props.maxItems;
});
const visibleItems = computed(() => items.value.slice(0, previewLimit.value));
const showAll = async (): Promise<void> => {
  if (expanding.value || !attachmentsReady.value) return;
  expanded.value = true;
  expanding.value = true;
  try {
    await Promise.all([loadRemainingPages(query), loadRemainingPages(additionalQuery)]);
  } finally {
    expanding.value = false;
  }
};
const loadRemainingPages = async (attachments: typeof query): Promise<void> => {
  while (attachments.hasNextPage.value) {
    const result = await attachments.fetchNextPage();
    if (result.isError) return;
  }
};
const remainingCount = computed(() => {
  if (!attachmentsReady.value) {
    const shown = Math.min(loadingItems.value.length, previewLimit.value ?? loadingItems.value.length);
    return Math.max(0, expectedAttachmentCount.value - shown);
  }
  const unloaded = (query.data.value?.totalCount ?? 0) - (query.data.value?.items.length ?? 0);
  const additionalUnloaded =
    props.readonly && props.additionalEntry
      ? (additionalQuery.data.value?.totalCount ?? 0) - (additionalQuery.data.value?.items.length ?? 0)
      : 0;
  return Math.max(0, items.value.length - visibleItems.value.length + unloaded + additionalUnloaded);
});
const chipAppearance = computed(() => (props.entryType === EntryAttachmentType.Note ? 'note' : 'ghost'));
const editable = computed(() => !props.readonly && (props.canEdit ?? query.data.value?.canEdit) === true);
const attachmentLimitReached = computed(
  () => (query.data.value?.totalCount ?? 0) >= ENTRY_ATTACHMENT_MAX_FILES_PER_ENTRY,
);
const loadFailed = computed(() => refreshFailed.value || query.isError.value || additionalQuery.isError.value);
const appPopover = useTemplateRef('appPopover');
const anchor = ref<HTMLElement | null>(null);
const selectedApps = computed(() =>
  (props.materials ?? []).map((material) => ({ externalMaterialId: material.externalMaterialId, item: material })),
);

const upload = async (): Promise<void> => {
  const destination = { ...target.value };
  if (!refreshFailed.value) savedItemsDuringUpload.value = [...(query.data.value?.items ?? [])];
  await flush(destination, refresh);
};
const selectFiles = (files: File[]): void => {
  add(files);
  void upload();
};
const removeApp = (externalMaterialId: number): void => {
  emit(
    'update:materials',
    materialIds.value.filter((id) => id !== externalMaterialId),
  );
};
const retry = (): void => {
  if (refreshFailed.value) void upload();
  else if (query.isError.value) void query.refetch();
  if (additionalQuery.isError.value) void additionalQuery.refetch();
};
const removeItem = (item: IAttachmentCardItem): void => {
  if (item.type === AttachmentContentType.App) removeApp(item.id);
  else void removeFile(item.id);
};
const openApps = (element: HTMLElement): void => {
  anchor.value = element;
  void appPopover.value?.show();
};
const selectApp = (app: Components.Schemas.RecommendedExternalMaterialDto): void => {
  if (!materialIds.value.includes(app.id)) emit('update:materials', [...materialIds.value, app.id]);
};
const displayedItems = computed(() =>
  attachmentsReady.value ? visibleItems.value : loadingItems.value.slice(0, previewLimit.value),
);
</script>

<template>
  <div ref="container" class="w-full min-w-0" @click="!previewOnly && $event.stopPropagation()">
    <div v-if="items.length || loadingItems.length || !readonly || loadFailed" class="flex flex-col gap-md">
      <AttachmentGrid
        v-if="displayedItems.length || pending.length || $slots.append"
        :items="displayedItems"
        :preview-only="previewOnly"
        :elevated="elevated"
        :scrollable="maxItems == null"
        :removable="editable"
        :disabled="saving"
        @remove="removeItem"
      >
        <PendingAttachmentGrid :items="pending" inline @remove="remove" />
        <slot name="append" />
      </AttachmentGrid>
      <button
        v-if="remainingCount && maxItems != null"
        type="button"
        class="self-start text-left text-base text-secondary underline outline-inner transition-colors hover:text-primary active:text-primary [text-decoration-skip-ink:none] [text-underline-position:from-font]"
        :disabled="!attachmentsReady || expanding"
        :aria-expanded="expanded"
        :aria-busy="expanding"
        @click.stop="showAll"
      >
        {{ $t('entryAttachment.more', { count: remainingCount }) }}
      </button>
      <button
        v-if="maxItems == null && !previewOnly && query.hasNextPage.value"
        type="button"
        class="text-link-primary self-start"
        :disabled="query.isFetchingNextPage.value"
        @click="query.fetchNextPage()"
      >
        {{ $t('entryAttachment.loadMore') }}
      </button>
      <button
        v-if="maxItems == null && !previewOnly && readonly && additionalEntry && additionalQuery.hasNextPage.value"
        type="button"
        class="text-link-primary self-start"
        :disabled="additionalQuery.isFetchingNextPage.value"
        @click="additionalQuery.fetchNextPage()"
      >
        {{ $t('entryAttachment.loadMore') }}
      </button>
      <div v-if="loadFailed" class="flex items-center gap-sm text-footer text-secondary" role="status">
        {{ $t('entryAttachment.loadFailed') }}
        <button type="button" class="text-link-primary" :disabled="uploading" @click="retry">
          {{ $t('entryAttachment.retry') }}
        </button>
      </div>
      <div
        v-if="!readonly && showActions"
        class="flex items-center gap-sm overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0 md:flex-wrap md:overflow-visible"
      >
        <AttachmentFilePicker
          :appearance="chipAppearance"
          :disabled="!editable || uploading || refreshFailed || attachmentLimitReached"
          @files="selectFiles"
        />
        <ComposerChip
          v-if="materials"
          :appearance="chipAppearance"
          :label="$t('supportMeasureEntry.materials.appChip')"
          :disabled="!editable || saving"
          @click="openApps"
        >
          <template #icon><ExternalMaterialIcon class="size-icon-xs shrink-0" /></template>
        </ComposerChip>
        <slot name="actions" />
      </div>
      <p v-if="editable && showActions && attachmentLimitReached" class="text-footer text-secondary">
        {{ $t('entryAttachment.limitReached', { count: ENTRY_ATTACHMENT_MAX_FILES_PER_ENTRY }) }}
      </p>
    </div>
    <ExternalMaterialSelectionPopover
      v-if="editable && materials && query.data.value"
      ref="appPopover"
      :anchor-element="anchor"
      mobile-drawer
      :school-subject-id="query.data.value.schoolSubjectId"
      :school-class-id="query.data.value.schoolClassId"
      :selected-materials="selectedApps"
      @add:material="selectApp"
      @remove:material="removeApp"
    />
  </div>
</template>
